import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CreditCard, FileText, Mail, MessageSquare, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function AdminIntegrations() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("ifthenpay");
    const [testTo, setTestTo] = useState({ email: "", whatsapp: "" });
    const [testing, setTesting] = useState({ email: false, whatsapp: false });

    useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

    const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
    const save = async () => {
        setBusy(true);
        try {
            await api.put("/admin/settings", s);
            toast.success("Integrações guardadas");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setBusy(false); }
    };

    const testEmail = async () => {
        if (!testTo.email) return toast.error("Indique destinatário");
        setTesting((t) => ({ ...t, email: true }));
        try {
            const { data } = await api.post("/admin/integrations/test-email", { to: testTo.email });
            if (data.sent) toast.success("Email enviado ✓");
            else toast.error(data.error || data.reason || "Não enviado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setTesting((t) => ({ ...t, email: false })); }
    };
    const testWA = async () => {
        if (!testTo.whatsapp) return toast.error("Indique número (+351...)");
        setTesting((t) => ({ ...t, whatsapp: true }));
        try {
            const { data } = await api.post("/admin/integrations/test-whatsapp", { to: testTo.whatsapp });
            if (data.sent) toast.success("WhatsApp enviado ✓");
            else toast.error(data.error || data.reason || "Não enviado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally { setTesting((t) => ({ ...t, whatsapp: false })); }
    };

    if (!s) return <div className="font-mono">A carregar…</div>;

    const tabs = [
        { k: "ifthenpay", l: "Ifthenpay", icon: CreditCard },
        { k: "moloni", l: "Moloni", icon: FileText },
        { k: "email", l: "Email (Resend)", icon: Mail },
        { k: "whatsapp", l: "WhatsApp (Twilio)", icon: MessageSquare },
    ];

    return (
        <div data-testid="admin-integrations-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Integrações</h1>
                    <p className="text-sm text-ink/60 mt-1">Pagamentos, faturação, email e notificações WhatsApp.</p>
                </div>
                <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-integrations-btn">
                    {busy && <Loader2 className="animate-spin" size={14} />} Guardar
                </button>
            </div>

            <div className="border-2 border-ink bg-white">
                <div className="border-b-2 border-ink flex flex-wrap">
                    {tabs.map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-r-2 last:border-r-0 border-ink inline-flex items-center gap-2 ${
                                tab === t.k ? "bg-magenta text-white" : "hover:bg-paper"
                            }`}
                            data-testid={`tab-${t.k}`}
                        >
                            <t.icon size={14} /> {t.l}
                        </button>
                    ))}
                </div>

                <div className="p-6 space-y-5">
                    {tab === "ifthenpay" && (
                        <>
                            <Note>
                                Obtenha as suas chaves em <a href="https://www.ifthenpay.com" target="_blank" rel="noreferrer" className="text-magenta underline">ifthenpay.com</a> · Backoffice → Chaves API. Cada método tem chave própria.
                            </Note>
                            <Field label="Chave Multibanco" v={s.ifthenpay_mb_key} on={(v) => upd("ifthenpay_mb_key", v)} placeholder="ex: ABCD-1234-EFGH" />
                            <Field label="Chave MB Way" v={s.ifthenpay_mbway_key} on={(v) => upd("ifthenpay_mbway_key", v)} />
                            <Field label="Chave Cartão de Crédito" v={s.ifthenpay_ccard_key} on={(v) => upd("ifthenpay_ccard_key", v)} />
                            <label className="block">
                                <span className="label-mono mb-2 block">Ambiente</span>
                                <select value={s.ifthenpay_environment || "sandbox"} onChange={(e) => upd("ifthenpay_environment", e.target.value)} className="input-edge w-auto">
                                    <option value="sandbox">Sandbox (testes)</option>
                                    <option value="production">Produção</option>
                                </select>
                            </label>
                        </>
                    )}

                    {tab === "moloni" && (
                        <>
                            <Note>
                                Configure em <a href="https://www.moloni.pt" target="_blank" rel="noreferrer" className="text-magenta underline">moloni.pt</a> → Definições → API. Necessário Client ID, Client Secret e Company ID.
                            </Note>
                            <Field label="Client ID" v={s.moloni_client_id} on={(v) => upd("moloni_client_id", v)} />
                            <Field label="Client Secret" v={s.moloni_client_secret} on={(v) => upd("moloni_client_secret", v)} type="password" />
                            <Field label="Company ID" v={s.moloni_company_id} on={(v) => upd("moloni_company_id", v)} />
                            <label className="block">
                                <span className="label-mono mb-2 block">Ambiente</span>
                                <select value={s.moloni_environment || "sandbox"} onChange={(e) => upd("moloni_environment", e.target.value)} className="input-edge w-auto">
                                    <option value="sandbox">Sandbox</option>
                                    <option value="production">Produção</option>
                                </select>
                            </label>
                        </>
                    )}

                    {tab === "email" && (
                        <>
                            <Note>
                                Crie chave em <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-magenta underline">resend.com/api-keys</a>. Verifique o domínio do remetente em Domains. Em testes pode usar <code className="bg-paper px-1">onboarding@resend.dev</code> mas só envia para emails verificados na conta.
                            </Note>
                            <Field label="Resend API Key" v={s.email_api_key} on={(v) => upd("email_api_key", v)} type="password" placeholder="re_..." />
                            <Field label="Email remetente (from)" v={s.email_sender} on={(v) => upd("email_sender", v)} placeholder="encomendas@sostoners.pt" />
                            <Field label="Email cópia admin (notificações)" v={s.email_admin_to} on={(v) => upd("email_admin_to", v)} placeholder="admin@sostoners.pt" />

                            <div className="border-t-2 border-ink pt-4 mt-4">
                                <div className="label-mono mb-3 text-magenta">Testar envio</div>
                                <div className="flex gap-2 flex-wrap">
                                    <input value={testTo.email} onChange={(e) => setTestTo((t) => ({ ...t, email: e.target.value }))}
                                           placeholder="email destino" className="input-edge flex-1 min-w-[260px]" data-testid="test-email-to" />
                                    <button onClick={testEmail} disabled={testing.email} className="btn-secondary" data-testid="test-email-btn">
                                        {testing.email ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Enviar teste
                                    </button>
                                </div>
                                <p className="text-[11px] font-mono text-ink/50 mt-2">Guarde primeiro a chave antes de testar.</p>
                            </div>
                        </>
                    )}

                    {tab === "whatsapp" && (
                        <>
                            <label className="block">
                                <span className="label-mono mb-2 block">Provider</span>
                                <select value={s.whatsapp_provider || "twilio"} onChange={(e) => upd("whatsapp_provider", e.target.value)} className="input-edge w-auto" data-testid="wa-provider-select">
                                    <option value="twilio">Twilio (oficial WhatsApp Business)</option>
                                    <option value="railway">Railway (gateway HTTP custom)</option>
                                </select>
                            </label>

                            {s.whatsapp_provider === "railway" ? (
                                <>
                                    <Note>
                                        Usa o teu gateway WhatsApp alojado em Railway. O backend faz <code className="bg-paper px-1">POST {`{url}`}</code> com headers <code className="bg-paper px-1">Authorization: Bearer {`{token}`}</code> e payload JSON <code className="bg-paper px-1">{`{ "to": "+351...", "message": "..." }`}</code> (envia também alias <code className="bg-paper px-1">phone/text</code> para compatibilidade).
                                    </Note>
                                    <Field label="URL do gateway Railway" v={s.whatsapp_railway_url} on={(v) => upd("whatsapp_railway_url", v)} placeholder="https://teu-projeto.up.railway.app/send" />
                                    <Field label="Bearer Token (opcional)" v={s.whatsapp_railway_token} on={(v) => upd("whatsapp_railway_token", v)} type="password" />
                                </>
                            ) : (
                                <>
                                    <Note>
                                        Crie conta em <a href="https://www.twilio.com" target="_blank" rel="noreferrer" className="text-magenta underline">twilio.com</a> e active WhatsApp Sandbox em Console → Messaging → Try WhatsApp.
                                    </Note>
                                    <Field label="Twilio Account SID" v={s.whatsapp_account_sid} on={(v) => upd("whatsapp_account_sid", v)} placeholder="AC..." />
                                    <Field label="Auth Token" v={s.whatsapp_auth_token} on={(v) => upd("whatsapp_auth_token", v)} type="password" />
                                    <Field label="Número remetente (WhatsApp)" v={s.whatsapp_from} on={(v) => upd("whatsapp_from", v)} placeholder="+14155238886" />
                                </>
                            )}
                            <Field label="Número admin para notificações" v={s.whatsapp_admin_to} on={(v) => upd("whatsapp_admin_to", v)} placeholder="+351912345678" />

                            <div className="border-t-2 border-ink pt-4 mt-4">
                                <div className="label-mono mb-3 text-magenta">Testar envio</div>
                                <div className="flex gap-2 flex-wrap">
                                    <input value={testTo.whatsapp} onChange={(e) => setTestTo((t) => ({ ...t, whatsapp: e.target.value }))}
                                           placeholder="+351912345678" className="input-edge flex-1 min-w-[260px]" data-testid="test-wa-to" />
                                    <button onClick={testWA} disabled={testing.whatsapp} className="btn-secondary" data-testid="test-wa-btn">
                                        {testing.whatsapp ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Enviar teste
                                    </button>
                                </div>
                                <p className="text-[11px] font-mono text-ink/50 mt-2">
                                    {s.whatsapp_provider === "railway"
                                        ? "Guarde antes de testar. Railway gateway deve responder 2xx."
                                        : "Em sandbox, o destinatário tem de juntar-se ao sandbox Twilio enviando uma palavra-chave."}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input type={type} value={v ?? ""} placeholder={placeholder || ""} onChange={(e) => on(e.target.value)} className="input-edge font-mono" />
        </label>
    );
}

function Note({ children }) {
    return (
        <div className="border-2 border-magenta/40 bg-magenta/5 p-3 text-xs">
            {children}
        </div>
    );
}
